'use client';

import React, { forwardRef, useEffect, useState, useCallback } from 'react';
import { clsx } from 'clsx';
import useEmblaCarousel from 'embla-carousel-react';
import { useBuyerProductStore } from 'app/store/product';
import { ImageGalleryViewport } from './ImageGalleryViewport';
import { ImageGalleryThumb } from './ImageGalleryThumb';
import { productImageGalleryTypeKey } from './define';
import { type ProductImageGalleryProps, type SlideOptions } from './ProductImageGallery';
import STYLE from './ProductImageGallery.module.scss';

export const Carousel = forwardRef<
    HTMLDivElement,
    ProductImageGalleryProps & SlideOptions
>((props, ref) => {
    const { isEditingMode, slideOptions, isBuyer } = props;

    const { lastChooseSpecMetaId } = useBuyerProductStore();

    const [selectedIndex, setSelectedIndex] = useState(0);

    const [emblaMainRef, emblaMainApi] = useEmblaCarousel({
        loop: slideOptions.type === productImageGalleryTypeKey.scroll,
    });

    const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
        containScroll: 'keepSnaps',
        dragFree: true,
        axis: slideOptions.thumbDirection === 'vertical' ? 'y' : 'x',
    });

    const onSelect = useCallback(
        (specMetaId?: string | null) => {
            if (!emblaMainApi || !emblaThumbsApi) {
                return;
            }

            if (specMetaId) {
                const index = slideOptions.medias.findIndex((i) => i.id === specMetaId);

                if (index !== -1) {
                    setSelectedIndex(index);

                    emblaMainApi.scrollTo(index);

                    emblaThumbsApi.scrollTo(index);

                    return;
                }
            }

            setSelectedIndex(emblaMainApi.selectedScrollSnap());

            emblaThumbsApi.scrollTo(emblaMainApi.selectedScrollSnap());
        },
        [emblaMainApi, emblaThumbsApi, setSelectedIndex, slideOptions]
    );

    useEffect(() => {
        if (!emblaMainApi) {
            return;
        }

        onSelect(lastChooseSpecMetaId);

        emblaMainApi.on('select', () => {
            onSelect();
        });

        emblaMainApi.on('reInit', () => {
            onSelect();
        });
    }, [emblaMainApi, onSelect, lastChooseSpecMetaId]);

    return (
        <div
            ref={ref}
            style={{
                ...(slideOptions.type === productImageGalleryTypeKey.scroll && {
                    '--slide-size': '85%',
                    '--slide-next-button-size': '32px',
                    '--slide-next-button-icon-size': '12px',
                }),
            }}
            className={clsx(
                STYLE.gallery,
                slideOptions.thumbDirection === 'vertical' && STYLE.galleryThumbsSide,
                isEditingMode && STYLE.galleryInactive
            )}
        >
            <div
                className={clsx(
                    slideOptions.thumbDirection === 'vertical' &&
                        STYLE.galleryThumbsSideBox
                )}
            >
                <ImageGalleryViewport
                    isBuyer={isBuyer}
                    viewportRef={emblaMainRef}
                    api={{ main: emblaMainApi }}
                    slideOptions={slideOptions}
                    isEditingMode={isEditingMode}
                />

                {slideOptions.type !== productImageGalleryTypeKey.scroll && (
                    <ImageGalleryThumb
                        isBuyer={isBuyer}
                        thumbRef={emblaThumbsRef}
                        selectedIndex={selectedIndex}
                        api={{
                            main: emblaMainApi,
                            thumb: emblaThumbsApi,
                        }}
                        slideOptions={slideOptions}
                        isEditingMode={isEditingMode}
                    />
                )}
            </div>
        </div>
    );
});

Carousel.displayName = 'ImageCarousel';
